import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    TouchSensor,
    PointerSensor,
    useSensor,
    useSensors,
    MouseSensor,
    DragStartEvent,
    closestCorners,
    pointerWithin,
    rectIntersection,
    getFirstCollision,
} from '@dnd-kit/core'
import React, { ComponentPropsWithRef } from 'react'
import { DragEndFn, DragStartFn, MySortableData } from './type'
import { collisionDetectionStrategy } from './collisionDetection'

type Props = {
    children: ComponentPropsWithRef<'div'>['children'] // React.ReactNode,
    onDragEnd: DragEndFn
    onDragOver?: DragEndFn // TODO
    onDragStart?: DragStartFn
    disabled?: boolean
    // https://docs.dndkit.com/presets/sortable#multiple-containers
    // https://master--5fc05e08a4a65d0021ae0bf2.chromatic.com/?path=/docs/presets-sortable-multiple-containers--basic-setup
    // https://github.com/clauderic/dnd-kit/blob/master/stories/2%20-%20Presets/Sortable/4-MultipleContainers.story.tsx
    multiContainer?: boolean // FIXME better handling of switching between multiContainer and simple container
}

export const DndContainer = (p: Props) => {
    const {
        onDragEnd,
        onDragStart,
        onDragOver,
        children,
        disabled = false,
        multiContainer = false,
    } = p
    // https://docs.dndkit.com/api-documentation/sensors
    // By default, DndContext uses the Pointer and Keyboard sensors.
    // https://docs.dndkit.com/api-documentation/sensors/pointer
    // If  the above recommendations are not suitable for your use-case,
    // we recommend that you use both the Mouse and Touch sensors instead,
    // as Touch events do not suffer the same limitations as Pointer events,
    // and it is possible to prevent the page from scrolling in touchmove events.
    // const pointerSensor = useSensor(PointerSensor)
    // https://docs.dndkit.com/api-documentation/sensors/touch
    const touchSensor = useSensor(TouchSensor)
    // https://docs.dndkit.com/api-documentation/sensors/mouse
    const mouseSensor = useSensor(MouseSensor)
    // Required to use both sensors !
    // https://docs.dndkit.com/api-documentation/sensors
    const sensors = useSensors(touchSensor, mouseSensor)
    // const localState = binsState.value;

    if (disabled) {
        return <div>{children}</div>
    }

    return (
        <DndContext
            sensors={sensors}
            // autoScroll={{layoutShiftCompensation: false}}
            // autoScroll={{ layoutShiftCompensation: false, enable: false }}
            // autoScroll={{ acceleration: 1 }}
            // Disable Horizontal scrolling
            autoScroll={{ threshold: { x: 0, y: 0.2 } }}
            collisionDetection={
                multiContainer ? collisionDetectionStrategy : pointerWithin
            }
            onDragStart={(e: DragStartEvent) => {
                onDragStart?.(
                    String(e.active.id),
                    String(e.active.data.current?.sourceContainerId)
                )
            }}
            onDragEnd={(e: DragEndEvent) => {
                const { active, over } = e
                if (over) {
                    const draggableData = e.active?.data?.current as MySortableData
                    const activeId = String(active.id)
                    const destData = e.over?.data?.current as MySortableData
                    const overId = String(over.id)
                    const sourceContainerId = draggableData?.sourceContainerId
                    const destContainerId = destData?.sourceContainerId

                    if (!sourceContainerId || !destContainerId) {
                        console.warn(
                            'Missing container data in drag operation',
                            {
                                draggableData,
                                destData,
                                activeId,
                                overId,
                            }
                        )
                        return
                    }

                    console.log(
                        'DndContext: Dropped "' +
                            activeId +
                            '" on ' +
                            overId +
                            ' from ' +
                            sourceContainerId +
                            ' to ' +
                            destContainerId
                    )

                    // Handle simple droppables (like RankingBin) vs sortable containers
                    if (
                        !multiContainer &&
                        sourceContainerId === destContainerId
                    ) {
                        console.log('Same container no change, ignoring', {
                            sourceContainerId,
                            destContainerId,
                        })
                        return
                    }

                    // Handle sortable containers - FIXME clearer logic
                    if (destData.sortable?.items?.includes(activeId)) {
                        const { items, index: newIndex } = destData.sortable
                        const oldIndex = items.findIndex(
                            (id: any) => String(id) === activeId
                        )
                        if (
                            oldIndex === -1 || // impossible
                            newIndex === undefined || // impossible
                            oldIndex === newIndex
                        ) {
                            console.log(
                                'Same position in sortable container, ignoring',
                                {
                                    oldIndex,
                                    newIndex,
                                    items,
                                }
                            )
                            return
                        }

                        console.log('Same container reorder:', {
                            newIndex,
                        })

                        onDragEnd(
                            activeId,
                            sourceContainerId,
                            sourceContainerId,
                            newIndex
                        )
                    } else if (destData.sortable) {
                        // Cross-container moves should be handled in onDragOver, not onDragEnd
                        // This prevents conflicts and ensures proper state updates
                        const { items, index: newIndex } = destData.sortable
                        console.log(
                            'Cross-container move detected in onDragEnd',
                            {
                                items,
                            }
                        )
                        onDragEnd(
                            activeId,
                            destContainerId,
                            sourceContainerId,
                            newIndex
                        )
                    } else {
                        console.log('Simple droppable move:', {
                            activeId,
                            sourceContainerId,
                            destContainerId,
                        })
                        onDragEnd(activeId, destContainerId, sourceContainerId)
                    }
                }
            }}
        >
            {children}
        </DndContext>
    )
}
